'use client';

import { useLiveGameSituation } from '@/hooks/useLiveStats';
import { ballOnLabel, downAndDistanceLabel, periodLabel } from '@/lib/liveStats';
import { cn } from '@/lib/utils';
import { OtnGame } from '@models/ot_nationals/otn_game';
import { OtnGameSituation } from '@models/ot_nationals/otn_live_stats';
import { Urls } from '@urls';
import Image from 'next/image';

type GameSituationProps = {
	game: OtnGame;
	initialSituation: OtnGameSituation | null;
};

const NEXT_KIND_LABELS: Record<string, string> = {
	try: 'Try',
	kickoff: 'Kickoff',
	free_kick: 'Free Kick',
};

/** Possession, down & distance and ball spot, kept live from the situation channel. */
const GameSituation = ({ game, initialSituation }: GameSituationProps) => {
	const situation = useLiveGameSituation(game.id, initialSituation, true);

	if (!situation) return null;
	if (situation.display_settings && situation.display_settings.situation === false) return null;
	if (situation.is_complete || situation.game_phase === 'final' || situation.game_phase === 'pregame') return null;

	const teams = game.otn_games_otn_teams ?? [];
	const possessionTeam = teams.find((t) => t.otn_team_id === situation.possession_otn_team_id)?.otn_team;
	const opponentTeam = teams.find((t) => t.otn_team_id !== situation.possession_otn_team_id)?.otn_team;
	const timeoutTeam = teams.find((t) => t.otn_team_id === situation.timeout_otn_team_id)?.otn_team;

	const isHalftime = situation.game_phase === 'halftime';
	const nextKindLabel = situation.next_kind ? NEXT_KIND_LABELS[situation.next_kind] : undefined;
	const downLabel =
		nextKindLabel ?? downAndDistanceLabel(situation.down, situation.distance, situation.is_goal_to_go) ?? null;
	const spotLabel = ballOnLabel(situation.yard_line, possessionTeam?.team_code, opponentTeam?.team_code);

	return (
		<section
			className="otn-section-border flex flex-col items-center gap-3 bg-black px-4 py-4 md:flex-row md:justify-between md:px-6"
			aria-label="Game situation"
			data-testid="game-situation"
		>
			<div className="flex items-center gap-3">
				{possessionTeam?.logo_path && !isHalftime && (
					<div className="relative h-8 w-8">
						<Image
							src={`${Urls.OvertimeImagesBaseUrl}${possessionTeam.logo_path}`}
							fill
							alt={`${possessionTeam.name || 'Possession team'} logo`}
						/>
					</div>
				)}
				<div className="font-beachwood text-[20px] leading-[120%] font-bold uppercase">
					{isHalftime ? 'Halftime' : (downLabel ?? '--')}
				</div>
				{!isHalftime && spotLabel && (
					<div className="text-otnGray text-[13px] leading-[150%] uppercase">
						{nextKindLabel ? `at ${spotLabel}` : `Ball on ${spotLabel}`}
					</div>
				)}
			</div>
			<div className="flex items-center gap-3">
				{situation.is_timeout && (
					<span
						className={cn(
							'bg-otnLime rounded-full px-3 py-1 text-[11px] leading-[120%] font-extrabold tracking-[1px] text-black uppercase',
						)}
					>
						Timeout{timeoutTeam?.team_code ? ` · ${timeoutTeam.team_code}` : ''}
					</span>
				)}
				{!isHalftime && periodLabel(situation.period) && (
					<span className="text-otnBlue font-beachwood text-[16px] leading-[120%] font-bold uppercase">
						{periodLabel(situation.period)}
					</span>
				)}
			</div>
		</section>
	);
};

export default GameSituation;
